import React, { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faStore, faSearch, faSync, faBuilding,
    faExternalLinkAlt, faTv, faDesktop, faCopy, faCheck
} from '@fortawesome/free-solid-svg-icons';
import { Spinner, Modal } from 'react-bootstrap';
import request from '../../../../request';
import Notify from '../../../compartidos/Notify';

const API_BASE    = process.env.REACT_APP_API_URL || 'http://localhost:5000/';
const ORIGIN      = window.location.origin + '/tufilaapp';

/**
 * SuperAdminSucursales — Global branches list.
 * Allows viewing installation URLs for any branch.
 */
const SuperAdminSucursales = () => {
    const [branches, setBranches]   = useState([]);
    const [loading, setLoading]     = useState(true);
    const [search, setSearch]       = useState('');
    const [compFilter, setCompFilter] = useState('');
    const [companies, setCompanies] = useState([]);
    const [modal, setModal]         = useState(null);   // branch for install modal
    const [copied, setCopied]       = useState('');

    useEffect(() => { fetchBranches(); }, []);

    const fetchBranches = () => {
        setLoading(true);
        request.Get(API_BASE + 'superadmin/branches', {}, r => {
            if (r.success) {
                const data = r.data || [];
                setBranches(data);
                // derive unique companies for filter
                const uniq = {};
                data.forEach(b => { if (b.EmpresaId) uniq[b.EmpresaId] = b.EmpresaNombre; });
                setCompanies(Object.entries(uniq).map(([id, name]) => ({ id, name })));
            } else {
                Notify({ type:'fail', text:'Error al cargar sucursales' });
            }
            setLoading(false);
        }, 'json', () => setLoading(false));
    };

    const filtered = useMemo(() => {
        return branches.filter(b => {
            const q = search.toLowerCase();
            const matchSearch = !q || b.SucursalNombre?.toLowerCase().includes(q) || b.EmpresaNombre?.toLowerCase().includes(q);
            const matchComp   = !compFilter || String(b.EmpresaId) === String(compFilter);
            return matchSearch && matchComp;
        });
    }, [branches, search, compFilter]);

    const handleCopy = (url) => {
        navigator.clipboard.writeText(url);
        setCopied(url);
        Notify({ type:'success', text:'URL copiada' });
        setTimeout(() => setCopied(''), 2000);
    };

    const totemUrl  = (b) => `${ORIGIN}/TurneroTotem?sucursalId=${b.SucursalId}`;
    const staffUrl  = () => `${ORIGIN}/TurneroStaff`;

    return (
        <div className="animate-in pb-5">
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                <div>
                    <h2 style={{ fontWeight:800, margin:0 }}>
                        <FontAwesomeIcon icon={faStore} style={{ color:'var(--sa-primary)', marginRight:'0.6rem' }} />
                        Sucursales
                    </h2>
                    <p className="text-muted small mt-1 mb-0">Vista global de todas las sucursales con acceso a instalación</p>
                </div>
                <button className="sa-btn sa-btn-ghost" onClick={fetchBranches} disabled={loading}>
                    <FontAwesomeIcon icon={faSync} spin={loading} /> Actualizar
                </button>
            </div>

            {/* Filters */}
            <div className="d-flex gap-3 mb-4 flex-wrap">
                <div className="sa-search-bar" style={{ flex:'1', maxWidth:'none' }}>
                    <FontAwesomeIcon icon={faSearch} style={{ color:'#475569' }} />
                    <input
                        placeholder="Buscar sucursal..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                </div>
                <select
                    className="sa-input"
                    style={{ width:'auto', minWidth:200 }}
                    value={compFilter}
                    onChange={e => setCompFilter(e.target.value)}
                >
                    <option value="">Todas las empresas</option>
                    {companies.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                </select>
            </div>

            <div className="sa-panel">
                {loading ? (
                    <div className="text-center py-5">
                        <Spinner animation="grow" style={{ color:'var(--sa-primary)' }} />
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="sa-empty">
                        <FontAwesomeIcon icon={faStore} style={{ fontSize:'3rem', opacity:0.15, display:'block', margin:'0 auto 1rem' }} />
                        <p>No se encontraron sucursales</p>
                    </div>
                ) : (
                    <div className="table-responsive">
                        <table className="sa-table">
                            <thead>
                                <tr>
                                    <th>Sucursal</th>
                                    <th>Empresa</th>
                                    <th>Tipos de Turno</th>
                                    <th>Personal</th>
                                    <th>Instalación</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map(b => (
                                    <tr key={b.SucursalId}>
                                        <td>
                                            <div className="d-flex align-items-center gap-2">
                                                <div style={{
                                                    width:32, height:32, borderRadius:8,
                                                    background:'rgba(59,130,246,0.1)',
                                                    display:'flex', alignItems:'center', justifyContent:'center',
                                                    color:'#3b82f6', flexShrink:0, fontSize:'0.8rem'
                                                }}>
                                                    <FontAwesomeIcon icon={faStore} />
                                                </div>
                                                <span style={{ fontWeight:600 }}>{b.SucursalNombre}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="d-flex align-items-center gap-1" style={{ color:'#94a3b8', fontSize:'0.85rem' }}>
                                                <FontAwesomeIcon icon={faBuilding} style={{ opacity:0.4 }} />
                                                {b.EmpresaNombre}
                                            </div>
                                        </td>
                                        <td>
                                            <span style={{ fontWeight:700 }}>{b.TotalTipos ?? 0}</span>
                                            <span style={{ color:'#475569', fontSize:'0.78rem', marginLeft:4 }}>tipos</span>
                                        </td>
                                        <td>
                                            <span style={{ fontWeight:700 }}>{b.TotalStaff ?? 0}</span>
                                            <span style={{ color:'#475569', fontSize:'0.78rem', marginLeft:4 }}>usuarios</span>
                                        </td>
                                        <td>
                                            <button
                                                className="sa-btn sa-btn-ghost sa-btn-sm"
                                                onClick={() => setModal(b)}
                                            >
                                                <FontAwesomeIcon icon={faExternalLinkAlt} /> Ver URLs
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Installation Modal */}
            <Modal show={!!modal} onHide={() => setModal(null)} centered size="lg" dialogClassName="sa-modal">
                <Modal.Header closeButton className="border-0">
                    <Modal.Title style={{ fontWeight:800, fontSize:'1.1rem', color:'#f8fafc' }}>
                        <FontAwesomeIcon icon={faStore} style={{ color:'var(--sa-primary)', marginRight:'0.5rem' }} />
                        Instalación — {modal?.SucursalNombre}
                        <small style={{ marginLeft:8, color:'#64748b', fontSize:'0.75rem', fontWeight:400 }}>
                            {modal?.EmpresaNombre}
                        </small>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {modal && (
                        <div className="d-flex flex-column gap-3">
                            {/* Totem */}
                            <div style={{ background:'rgba(168,85,247,0.04)', border:'1px solid rgba(168,85,247,0.12)', borderRadius:12, padding:'1rem 1.25rem' }}>
                                <div className="d-flex align-items-center gap-2 mb-2">
                                    <FontAwesomeIcon icon={faDesktop} style={{ color:'var(--sa-primary)' }} />
                                    <strong style={{ fontSize:'0.85rem' }}>Totem / Kiosco</strong>
                                </div>
                                <div className="d-flex gap-2">
                                    <input className="sa-input" readOnly value={totemUrl(modal)} style={{ flex:1, fontSize:'0.78rem' }} />
                                    <button className="sa-btn sa-btn-ghost" onClick={() => handleCopy(totemUrl(modal))}>
                                        <FontAwesomeIcon icon={copied === totemUrl(modal) ? faCheck : faCopy} />
                                    </button>
                                    <a href={totemUrl(modal)} target="_blank" rel="noopener noreferrer" className="sa-btn sa-btn-ghost">
                                        <FontAwesomeIcon icon={faExternalLinkAlt} />
                                    </a>
                                </div>
                            </div>

                            {/* Staff */}
                            <div style={{ background:'rgba(16,185,129,0.04)', border:'1px solid rgba(16,185,129,0.12)', borderRadius:12, padding:'1rem 1.25rem' }}>
                                <div className="d-flex align-items-center gap-2 mb-2">
                                    <FontAwesomeIcon icon={faTv} style={{ color:'#10b981' }} />
                                    <strong style={{ fontSize:'0.85rem' }}>Consola de Staff</strong>
                                </div>
                                <div className="d-flex gap-2">
                                    <input className="sa-input" readOnly value={staffUrl()} style={{ flex:1, fontSize:'0.78rem' }} />
                                    <button className="sa-btn sa-btn-ghost" onClick={() => handleCopy(staffUrl())}>
                                        <FontAwesomeIcon icon={copied === staffUrl() ? faCheck : faCopy} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className="border-0">
                    <button className="sa-btn sa-btn-ghost" onClick={() => setModal(null)}>Cerrar</button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default SuperAdminSucursales;
